import DOMPurify from 'dompurify';
import { convertFromShortcodes } from '../../../../../Queue/utils/emojiParser';
import { mentionUtils } from '../../../../../Queue/CMComposer/mentionUtils';
import { User } from '@hone-automation/common';

export interface FormatMessageContentOptions {
  users?: User[];
  fallbackUser?: {
    userId: string;
    name: string;
  };
  skipMentionFormatting?: boolean;
}

const purifyConfig = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'strike', 'del', 'div'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
  ADD_ATTR: ['target', 'rel'],
  KEEP_CONTENT: true,
};

const formatUrlsInText = (text: string): string => {
  const urlRegex = /(https?:\/\/[^\s<>"]+)/g;

  return text.replace(urlRegex, (match, url, offset) => {
    const beforeUrl = text.substring(Math.max(0, offset - 20), offset);
    const afterUrl = text.substring(offset + match.length, Math.min(text.length, offset + match.length + 20));

    if (/href\s*=\s*["']?$/.test(beforeUrl)) {
      return match;
    }

    if (/<a[^>]*$/.test(beforeUrl) && /^[^<]*<\/a>/.test(afterUrl)) {
      return match;
    }

    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};

const addLinkTargetHook = (node: Element) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
};

export const formatMessageContentToHtml = (content: string, options: FormatMessageContentOptions = {}): string => {
  if (!content) {
    return '';
  }

  DOMPurify.addHook('afterSanitizeAttributes', addLinkTargetHook);
  const sanitized = DOMPurify.sanitize(content, purifyConfig);
  DOMPurify.removeHook('afterSanitizeAttributes');

  const withEmojis = convertFromShortcodes(sanitized);
  const withLinks = formatUrlsInText(withEmojis);

  if (options.skipMentionFormatting) {
    return withLinks;
  }

  return mentionUtils.formatMentionsToHtml(withLinks, options.users, options.fallbackUser, 'q2-mention');
};

export const formatMessageContentToPlainText = (content: string, options: FormatMessageContentOptions = {}): string => {
  const html = formatMessageContentToHtml(content, options);
  if (!html) {
    return '';
  }

  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};
